import {
  ColorValue,
  NumericValue,
  PropertyTypeValueMap,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { settingsParamsDefinitions } from './settingsParams';
import { generateCustomCssVars } from './stylesParamsUtils';

export type IStylesParams = {
  topAndBottomPadding: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  widgetBorderColor: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetBorderRadius: StyleParamType.Number;
  widgetBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerColor: StyleParamType.Color;
  sectionsGap: StyleParamType.Number;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  sectionTitleColor: StyleParamType.Color;
  primaryTextFont: StyleParamType.Font;
  primaryTextColor: StyleParamType.Color;
  secondaryTextFont: StyleParamType.Font;
  secondaryTextColor: StyleParamType.Color;
  actionColor: StyleParamType.Color;
  disabledColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  borderRadius: StyleParamType.Number;
  dateTextFont: StyleParamType.Font;
  dateTextColor: StyleParamType.Color;
  timeSelectionTextColor: StyleParamType.Color;
  timeSelectionTextFont: StyleParamType.Font;
  timeSelectionBackgroundColor: StyleParamType.Color;
  bookButtonTextColor: StyleParamType.Color;
  bookButtonBGColor: StyleParamType.Color;
  bookButtonBorderColor: StyleParamType.Color;
  bookButtonHoverBorderColor: StyleParamType.Color;
  bookButtonHoverTextColor: StyleParamType.Color;
  bookButtonHoverBGColor: StyleParamType.Color;
  bookButtonTextFont: StyleParamType.Font;
  slotBackgroundColor: StyleParamType.Color;
  slotPrimaryTextFont: StyleParamType.Font;
  slotPrimaryTextColor: StyleParamType.Color;
  slotNameTextFont: StyleParamType.Font;
  slotNameTextColor: StyleParamType.Color;
  slotTimeTextFont: StyleParamType.Font;
  slotTimeTextColor: StyleParamType.Color;
  slotSecondaryTextFont: StyleParamType.Font;
  slotSecondaryTextColor: StyleParamType.Color;
  slotBorderWidth: StyleParamType.Number;
  slotBorderColor: StyleParamType.Color;
  servicePrimaryTextColor0: StyleParamType.Color;
  servicePrimaryTextColor1: StyleParamType.Color;
  servicePrimaryTextColor2: StyleParamType.Color;
  servicePrimaryTextColor3: StyleParamType.Color;
  servicePrimaryTextColor4: StyleParamType.Color;
  servicePrimaryTextColor5: StyleParamType.Color;
  servicePrimaryTextColor6: StyleParamType.Color;
  servicePrimaryTextColor7: StyleParamType.Color;
  servicePrimaryTextColor8: StyleParamType.Color;
  servicePrimaryTextColor9: StyleParamType.Color;
  serviceBackgroundColor0: StyleParamType.Color;
  serviceBackgroundColor1: StyleParamType.Color;
  serviceBackgroundColor2: StyleParamType.Color;
  serviceBackgroundColor3: StyleParamType.Color;
  serviceBackgroundColor4: StyleParamType.Color;
  serviceBackgroundColor5: StyleParamType.Color;
  serviceBackgroundColor6: StyleParamType.Color;
  serviceBackgroundColor7: StyleParamType.Color;
  serviceBackgroundColor8: StyleParamType.Color;
  serviceBackgroundColor9: StyleParamType.Color;
  mobilePageTitleFontSize: StyleParamType.Number;
  mobileSectionTitleFontSize: StyleParamType.Number;
  mobilePrimaryTextFontSize: StyleParamType.Number;
  mobileSecondaryTextFontSize: StyleParamType.Number;
  mobileDateTextFontSize: StyleParamType.Number;
  mobileTimeSelectionTextFontSize: StyleParamType.Number;
  mobileBookButtonTextFontSize: StyleParamType.Number;
  mobileSlotPrimaryTextFontSize: StyleParamType.Number;
  mobileSlotTimeTextFontSize: StyleParamType.Number;
  mobileSlotNameTextFontSize: StyleParamType.Number;
  mobileSlotSecondaryTextFontSize: StyleParamType.Number;
  dataAndTimeDividerColor: StyleParamType.Color;
  serviceDetailsDividerColor: StyleParamType.Color;
  dataAndTimeDividerWidth: StyleParamType.Number;
  serviceDetailsDividerWidth: StyleParamType.Number;
  dateAndTimeSectionTitleColor: StyleParamType.Color;
  dateAndTimeSectionTitleFont: StyleParamType.Font;
  dateAndTimePrimaryTextColor: StyleParamType.Color;
  dateAndTimePrimaryTextFont: StyleParamType.Font;
  dateAndTimeSecondaryTextColor: StyleParamType.Color;
  dateAndTimeSecondaryTextFont: StyleParamType.Font;
  serviceDetailsSectionTitleColor: StyleParamType.Color;
  serviceDetailsSectionTitleFont: StyleParamType.Font;
  serviceDetailsPrimaryTextColor: StyleParamType.Color;
  serviceDetailsPrimaryTextFont: StyleParamType.Font;
  serviceDetailsSecondaryTextColor: StyleParamType.Color;
  serviceDetailsSecondaryTextFont: StyleParamType.Font;
  bookButtonCornerRadius: StyleParamType.Number;
} & IStyleParamsBoolean &
  IStyleParamsVariants;

export type IStyleParamsVariants = {
  headerSubtitleSource: StyleParamType.Number;
};

export type IStyleParamsBoolean = {
  headerSubtitleVisibility: StyleParamType.Boolean;
  headerTitleVisibility: StyleParamType.Boolean;
  headerLocationFilterVisibility: StyleParamType.Boolean;
  headerStaffFilterVisibility: StyleParamType.Boolean;
  headerServiceFilterVisibility: StyleParamType.Boolean;
  headerVisibility: StyleParamType.Boolean;
  headerTimezoneSelectionVisibility: StyleParamType.Boolean;
  timeslotDetailsVisibility: StyleParamType.Boolean;
  videoConferenceBadgeVisibility: StyleParamType.Boolean;
  dateAndTimeTimezoneSelectionVisibility: StyleParamType.Boolean;
};

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

export const booleanParam = (
  defaultValue: boolean | undefined,
): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue as boolean,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue as boolean,
    },
  },
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

export const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

export const numberParamWithMobileDefault = (
  type: StyleTypeFor<number>,
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static',
      value: mobileDefaultValue,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

export const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
    },
  },
});

export const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
      opacity,
    },
  },
});

export const colorRefParam = (
  otherColorParam: ColorStyleParam,
  otherColorParamName: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherColorParam),
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'ref',
      ref: otherColorParamName,
    },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

export const fontParam = (
  preset: keyof ISiteTextPreset,
  size: number,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, { size }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      size,
    },
  },
});

export const fontRefParam = (
  otherFontParam: FontStyleParam,
  otherFontParamName: string,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontParam),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'ref',
      ref: otherFontParamName,
    },
  },
});

export const fontSizeRefParam = (
  otherFontSizeParam: NumberStyleParam,
  otherFontSizeParamName: string,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontSizeParam),
  metadata: {
    type: StyleType.fontSize,
    defaultValue: {
      type: 'ref',
      ref: otherFontSizeParamName,
    },
  },
});

export const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset = 'Body-M',
  mobileDefaultSize: number = 14,
  defaultSize: number = 16,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({
    textPresets,
    isMobile,
  }: {
    textPresets: ISiteTextPreset;
    isMobile: boolean;
  }) =>
    wixFontParam(preset, {
      size: isMobile ? mobileDefaultSize : defaultSize,
    })({ textPresets }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      size: defaultSize,
    },
    mobileDefaultValue: {
      type: 'static',
      preset,
      size: mobileDefaultSize,
    },
  },
});

const numberRefParam = (
  otherNumberParam: NumberStyleParam,
  otherNumberParamName: string,
  type: StyleType.borderWidth | StyleType.borderRadius,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherNumberParam),
  metadata: {
    type,
    defaultValue: {
      type: 'ref',
      ref: otherNumberParamName,
    },
  },
});

const slotPrimaryTextFont = createStylesParam(
  'slotPrimaryTextFont',
  fontParam('Body-M', 16),
);

const slotPrimaryTextColor = createStylesParam(
  'slotPrimaryTextColor',
  colorParam(StyleType.color, 'color-5'),
);

const mobileSlotPrimaryTextFontSize = createStylesParam(
  'mobileSlotPrimaryTextFontSize',
  numberParam(StyleType.fontSize, 14),
);

const dividerColor = createStylesParam(
  'dividerColor',
  colorParam(StyleType.color, 'color-5'),
);

const dividerWidth = createStylesParam(
  'dividerWidth',
  numberParam(StyleType.borderWidth, 1),
);

const sectionTitleColor = createStylesParam(
  'sectionTitleColor',
  colorParam(StyleType.color, 'color-5'),
);

const sectionTitleFont = createStylesParam(
  'sectionTitleFont',
  fontParam('Page-title', 20),
);

const primaryTextColor = createStylesParam(
  'primaryTextColor',
  colorParam(StyleType.color, 'color-5'),
);

const primaryTextFont = createStylesParam(
  'primaryTextFont',
  fontParam('Body-M', 14),
);

const secondaryTextColor = createStylesParam(
  'secondaryTextColor',
  colorParam(StyleType.color, 'color-4'),
);

const secondaryTextFont = createStylesParam(
  'secondaryTextFont',
  fontParam('Body-M', 14),
);

const borderRadius = createStylesParam(
  'borderRadius',
  numberParam(StyleType.borderRadius, 0),
);

export const styleParamsThatOverrideSettings: {
  [key in keyof (IStyleParamsBoolean & IStyleParamsVariants)]: IStyleParam;
} = {
  headerSubtitleVisibility: booleanParam(undefined),
  headerTitleVisibility: booleanParam(undefined),
  headerLocationFilterVisibility: booleanParam(undefined),
  headerStaffFilterVisibility: booleanParam(undefined),
  headerServiceFilterVisibility: booleanParam(undefined),
  headerVisibility: booleanParam(undefined),
  headerTimezoneSelectionVisibility: booleanParam(undefined),
  headerSubtitleSource: numberParam(
    StyleType.UNKNOWN_CssPropertyType as any,
    undefined,
  ),
  timeslotDetailsVisibility: booleanParam(undefined),
  videoConferenceBadgeVisibility: booleanParam(undefined),
  dateAndTimeTimezoneSelectionVisibility: booleanParam(undefined),
};

const styleParamsDefinitions: { [key in keyof IStylesParams]: IStyleParam } = {
  topAndBottomPadding: numberParamWithMobileDefault(StyleType.padding, 24, 48),
  sidePadding: {
    inheritDesktop: false,
    ...numberParam(StyleType.padding, 20),
  },
  widgetBorderWidth: numberParam(StyleType.borderWidth, 0),
  widgetBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
  ),
  widgetBorderRadius: numberParam(StyleType.borderRadius, 0),
  widgetBackgroundColor: colorParam(StyleType.backgroundColor, 'color-1'),
  headerBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
  ),
  dividerWidth: numberParam(StyleType.borderWidth, 1),
  dividerColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  sectionsGap: {
    inheritDesktop: false,
    ...numberParam(StyleType.gap, 44),
  },
  pageTitleColor: colorParam(StyleType.color, 'color-5'),
  pageTitleFont: fontParam('Page-title', 32),
  sectionTitleColor: colorParam(StyleType.color, 'color-5'),
  sectionTitleFont: fontParam('Page-title', 20),
  primaryTextColor: colorParam(StyleType.color, 'color-5'),
  primaryTextFont: fontParamWithMobileDefault(),
  secondaryTextColor: colorParam(StyleType.color, 'color-4'),
  secondaryTextFont: fontParam('Body-M', 14),
  actionColor: colorParam(StyleType.color, 'color-8'),
  disabledColor: colorParam(StyleType.color, 'color-3'),
  borderWidth: numberParam(StyleType.borderWidth, 1),
  borderRadius: numberParam(StyleType.borderRadius, 0),
  dateTextColor: colorParam(StyleType.color, 'color-5'),
  dateTextFont: fontParamWithMobileDefault(),
  timeSelectionTextFont: fontParamWithMobileDefault(),
  timeSelectionTextColor: colorParam(StyleType.color, 'color-5'),
  timeSelectionBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
  ),
  bookButtonTextColor: colorParam(StyleType.color, 'color-1'),
  bookButtonBGColor: colorParam(StyleType.backgroundColor, 'color-8'),
  bookButtonHoverBGColor: colorParam(StyleType.backgroundColor, 'color-8'),
  bookButtonBorderColor: colorParam(StyleType.borderColor, 'color-8'),
  bookButtonHoverBorderColor: colorParam(StyleType.borderColor, 'color-8'),
  bookButtonHoverTextColor: colorParam(StyleType.color, 'color-1'),
  bookButtonTextFont: fontParamWithMobileDefault(),
  slotPrimaryTextFont,
  slotPrimaryTextColor,
  slotNameTextFont: fontRefParam(slotPrimaryTextFont, 'slotPrimaryTextFont'),
  slotNameTextColor: colorRefParam(
    slotPrimaryTextColor,
    'slotPrimaryTextColor',
  ),
  slotTimeTextFont: fontRefParam(slotPrimaryTextFont, 'slotPrimaryTextFont'),
  slotTimeTextColor: colorRefParam(
    slotPrimaryTextColor,
    'slotPrimaryTextColor',
  ),
  slotSecondaryTextFont: fontParam('Body-M', 14),
  slotSecondaryTextColor: colorParam(StyleType.color, 'color-4'),
  mobilePageTitleFontSize: numberParam(StyleType.fontSize, 32),
  mobileSectionTitleFontSize: numberParam(StyleType.fontSize, 20),
  mobilePrimaryTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileSecondaryTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileDateTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileTimeSelectionTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileBookButtonTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileSlotPrimaryTextFontSize,
  mobileSlotNameTextFontSize: fontSizeRefParam(
    mobileSlotPrimaryTextFontSize,
    'mobileSlotPrimaryTextFontSize',
  ),
  mobileSlotTimeTextFontSize: fontSizeRefParam(
    mobileSlotPrimaryTextFontSize,
    'mobileSlotPrimaryTextFontSize',
  ),
  mobileSlotSecondaryTextFontSize: numberParam(StyleType.fontSize, 14),
  slotBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
  ),
  slotBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.3),
  slotBorderWidth: numberParam(StyleType.borderWidth, 1),
  servicePrimaryTextColor0: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor1: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor2: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor3: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor4: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor5: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor6: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor7: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor8: colorParam(StyleType.color, 'color-8'),
  servicePrimaryTextColor9: colorParam(StyleType.color, 'color-8'),
  serviceBackgroundColor0: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor1: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor2: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor3: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor4: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor5: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor6: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor7: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor8: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  serviceBackgroundColor9: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
  ),
  dataAndTimeDividerColor: colorRefParam(dividerColor, 'dividerColor'),
  serviceDetailsDividerColor: colorRefParam(dividerColor, 'dividerColor'),
  dataAndTimeDividerWidth: numberRefParam(
    dividerWidth,
    'dividerWidth',
    StyleType.borderWidth,
  ),
  serviceDetailsDividerWidth: numberRefParam(
    dividerWidth,
    'dividerWidth',
    StyleType.borderWidth,
  ),
  dateAndTimeSectionTitleColor: colorRefParam(
    sectionTitleColor,
    'sectionTitleColor',
  ),
  dateAndTimeSectionTitleFont: fontRefParam(
    sectionTitleFont,
    'sectionTitleFont',
  ),
  serviceDetailsSectionTitleColor: colorRefParam(
    sectionTitleColor,
    'sectionTitleColor',
  ),
  serviceDetailsSectionTitleFont: fontRefParam(
    sectionTitleFont,
    'sectionTitleFont',
  ),
  dateAndTimePrimaryTextColor: colorRefParam(
    primaryTextColor,
    'primaryTextColor',
  ),
  dateAndTimePrimaryTextFont: fontRefParam(primaryTextFont, 'primaryTextFont'),
  dateAndTimeSecondaryTextColor: colorRefParam(
    secondaryTextColor,
    'secondaryTextColor',
  ),
  dateAndTimeSecondaryTextFont: fontRefParam(
    secondaryTextFont,
    'secondaryTextFont',
  ),
  serviceDetailsPrimaryTextColor: colorRefParam(
    primaryTextColor,
    'primaryTextColor',
  ),
  serviceDetailsPrimaryTextFont: fontRefParam(
    primaryTextFont,
    'primaryTextFont',
  ),
  serviceDetailsSecondaryTextColor: colorRefParam(
    secondaryTextColor,
    'secondaryTextColor',
  ),
  serviceDetailsSecondaryTextFont: fontRefParam(
    secondaryTextFont,
    'secondaryTextFont',
  ),
  bookButtonCornerRadius: numberRefParam(
    borderRadius,
    'borderRadius',
    StyleType.borderRadius,
  ),
  ...styleParamsThatOverrideSettings,
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);

export const customCssVars: CustomCssVarsFn = (args) => {
  return generateCustomCssVars(args, settingsParamsDefinitions);
};
